import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Chip, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { DeleteForever, Edit, Forum, History, LocalLibrary } from '@mui/icons-material';
import { BackButton, StyledLink } from '../Components';
import LoadingSpinner from '../LoadingSpinner';
import { formatDate } from '../Formatters';
import { StyledPaper } from '../theme';
import { useModal } from '../dialogs/useModal';
import { RichText, htmlIsEmpty } from '../components/richText';
import { Thread } from '../comments/CommentThread';
import { GetTermQuery } from '../__generated__/graphql';
import { DELETE, GET, GET_ALL } from './dictionary.graphql';
import { TermDialog, TermDialogInput } from './TermDialog';
import { Comments } from './Comments';
import { TermHistory } from './TermHistory';
import { useCanEditDictionary } from './useCanEditDictionary';
import { TermlexDefinitionPageList, useTermlexDefinitions } from './TermlexDefinitions';

type Term = NonNullable<GetTermQuery['term']>;

export function Details() {
  const { id } = useParams();
  const { data, loading, refetch } = useQuery(GET, { variables: { id } });

  if (loading) return <LoadingSpinner />;
  if (!data?.term) return null;

  return <TermPage term={data.term} onUpdate={() => refetch()} />;
}

function TermPage({ term, onUpdate }: { term: Term; onUpdate: () => Promise<unknown> }) {
  const [tab, setTab] = useState<'discussions' | 'history' | 'termlex'>('discussions');
  const termlex = useTermlexDefinitions(term.preferred ?? '');
  const canEdit = useCanEditDictionary();
  const navigate = useNavigate();
  const editModal = useModal<TermDialogInput, string | undefined>({ data: { term } });
  const [deleteTerm] = useMutation(DELETE, { refetchQueries: [{ query: GET_ALL }] });

  const edit = async () => {
    if (await editModal.open({ term })) await onUpdate();
  };

  const remove = async () => {
    if (!window.confirm(`Slette begrepet «${term.preferred}»?`)) return;
    await deleteTerm({ variables: { input: { id: term.id } } });
    navigate('/dictionary');
  };

  const fact = (label: string, content: React.ReactNode) => (
    <div>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {content}
    </div>
  );
  const none = <Typography color="textSecondary">Ingen</Typography>;

  return (
    <Stack spacing={2} sx={{ maxWidth: 1000 }}>
      <StyledPaper>
        <Stack direction="row" alignItems="center">
          <BackButton to="/dictionary" />
          <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
            {term.preferred}
          </Typography>
          {canEdit && (
            <>
              <IconButton title="Endre begrep" onClick={edit}>
                <Edit />
              </IconButton>
              <IconButton title="Slett begrep" onClick={remove}>
                <DeleteForever />
              </IconButton>
            </>
          )}
        </Stack>

        <Box sx={{ mt: 3, ml: 2.5, pl: 2, borderLeft: 3, borderColor: 'primary.main', maxWidth: '75ch' }}>
          <Typography variant="body2" color="textSecondary">
            Definisjon
          </Typography>
          {htmlIsEmpty(term.definition) ? (
            none
          ) : (
            <Box sx={{ fontSize: 18, lineHeight: 1.7, '& p': { my: 0.5 } }}>
              <RichText html={term.definition} component="div" />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 3, pl: 2.5, maxWidth: '75ch' }}>
          <Typography variant="body2" color="textSecondary">
            Merknad om bruk
          </Typography>
          {htmlIsEmpty(term.notes) ? (
            none
          ) : (
            <Box sx={{ '& p': { my: 0.5 } }}>
              <RichText html={term.notes} component="div" />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 3, pl: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
          {fact(
            'Alternativ (vi velger ikke)',
            term.synonyms?.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {term.synonyms.map((s) => (
                  <Chip key={s} label={s} size="small" />
                ))}
              </Stack>
            ) : (
              none
            ),
          )}
          {fact(
            'Fagområder',
            term.categories?.length ? (
              <Stack spacing={0.5}>
                {term.categories.map((c) => (
                  <Typography key={c?.id}>
                    <StyledLink to={`/category/${c?.id}`}>{c?.title}</StyledLink>
                  </Typography>
                ))}
              </Stack>
            ) : (
              none
            ),
          )}
          {fact(
            'Se også',
            term.seeAlso?.length ? (
              <Stack spacing={0.5}>
                {term.seeAlso.map((t) => (
                  <Typography key={t?.id}>
                    <StyledLink to={`/dictionary/${t?.id}`}>{t?.preferred}</StyledLink>
                  </Typography>
                ))}
              </Stack>
            ) : (
              none
            ),
          )}
        </Box>

        <Typography variant="caption" color="textSecondary" component="p" sx={{ mt: 3, pl: 2.5 }}>
          Sist oppdatert av {term.updatedBy?.name ?? 'ukjent'}, {formatDate(term.updatedAt)}
        </Typography>
      </StyledPaper>

      <StyledPaper sx={{ pt: 0 }}>
        <Tabs value={tab} onChange={(_e, value) => setTab(value)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab
            value="discussions"
            icon={<Forum fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 48 }}
            label={`Diskusjoner (${term.comments?.length ?? 0})`}
          />
          <Tab
            value="history"
            icon={<History fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 48 }}
            label={`Historikk (${term.versions?.length ?? 0})`}
          />
          <Tab
            value="termlex"
            icon={<LocalLibrary fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 48 }}
            label={termlex.done ? `Termlex (${termlex.definitions.length})` : 'Termlex'}
          />
        </Tabs>
        {tab === 'discussions' && (
          <Comments term={term} comments={(term.comments ?? []).filter((c): c is NonNullable<typeof c> => !!c) as Thread[]} onUpdate={onUpdate} />
        )}
        {tab === 'termlex' && (
          <>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" component="h2">
                Definisjoner av «{termlex.query}» i Termlex
              </Typography>
            </Stack>
            <TermlexDefinitionPageList result={termlex} />
          </>
        )}
        {tab === 'history' && (
          <TermHistory
            termId={term.id}
            versions={(term.versions ?? []).filter((v): v is NonNullable<typeof v> => !!v)}
            canEdit={canEdit}
            onUpdate={onUpdate}
          />
        )}
      </StyledPaper>
      {editModal.isOpen && <TermDialog modal={editModal} />}
    </Stack>
  );
}
